import _ from 'lodash'
import { computed, ref, watch } from 'vue'

import { axios } from '@/utils/axios'

export const LOCAL_SEARCH_MIN_CHARS = 3
export const WEAK_RESULT_COUNT = 2

export const SUPPORTED_PLATFORM_OPTIONS = [
    { id: 'all', label: 'All', icon: null },
    { id: 'instagram', label: 'Instagram', icon: 'instagram', iconClass: 'text-instagram' },
    { id: 'tiktok', label: 'TikTok', icon: 'tiktok', iconClass: 'text-tiktok' },
    { id: 'youtube', label: 'YouTube', icon: 'youtube', iconClass: 'text-youtube' },
    { id: 'twitch', label: 'Twitch', icon: 'twitch', iconClass: 'text-twitch' },
    { id: 'twitter', label: 'X', icon: 'x-twitter', iconClass: 'text-x' },
]

const PLATFORM_IDS = SUPPORTED_PLATFORM_OPTIONS.filter((platform) => platform.id !== 'all').map((platform) => platform.id)
const LOCAL_ENDPOINT = '/discover/local'
const CACHE_DURATION = 5 * 60 * 1000
const SEARCH_DEBOUNCE_MS = 180
export const DEFAULT_PLATFORM_ID = 'instagram'

const createCacheEntry = (results) => ({
    timestamp: Date.now(),
    results,
})

const normalizeSearchText = (value) => (value || '').trim()

const normalizeForCompare = (value) =>
    normalizeSearchText(value)
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')

export const isSupportedPlatformId = (platformId) =>
    SUPPORTED_PLATFORM_OPTIONS.some((platform) => platform.id === platformId)

export const getPreferredGlobalSearchPlatformId = (...platformIds) =>
    platformIds.find((platformId) => isSupportedPlatformId(platformId)) || DEFAULT_PLATFORM_ID

const getPlatformIds = (platformId) => (platformId === 'all' ? PLATFORM_IDS : [platformId])

const getExternalEndpoint = (platformId) => `/discover/${platformId}/name`

const getResultKey = (platformId, creatorId) => `${platformId}:${creatorId}`

const getMatchQuality = (query, ...values) => {
    const normalizedQuery = normalizeForCompare(query)
    const normalizedValues = values.filter(Boolean).map(normalizeForCompare)

    if (normalizedValues.some((value) => value === normalizedQuery)) return 'exact'
    if (normalizedValues.some((value) => value.startsWith(normalizedQuery))) return 'prefix'
    if (normalizedValues.some((value) => value.includes(normalizedQuery))) return 'contains'

    return 'related'
}

const getRankGroup = (matchQuality) => {
    const groups = {
        exact: 0,
        prefix: 1,
        contains: 2,
        related: 3,
    }

    return groups[matchQuality] ?? groups.related
}

const normalizeInstagramAvatarUrl = (avatarUrl, platformId) => {
    if (platformId !== 'instagram' || !avatarUrl) return avatarUrl

    const avatarProxyUrl = `${axios.defaults.baseURL}/avatars?url=`

    if (avatarUrl.startsWith(avatarProxyUrl)) return avatarUrl

    return `${avatarProxyUrl}${encodeURIComponent(avatarUrl)}`
}

const normalizeCreatorResult = (creator, { query, source, index }) => {
    const superview = creator?.item?.superview || creator?.superview
    const normalized = superview?.normalized || {}
    const platformId = superview?.platform_id || creator?.platform_id
    const creatorId = superview?._id || creator?._id || normalized?._id

    if (!platformId || !creatorId) return null

    const displayName = normalized.display_name || normalized.name || normalized.socials?.twitter?.platform_user_name || ''
    const handle = normalized.name || normalized.platform_user_name || normalized.socials?.twitter?.platform_user_name || ''
    const followers = Number(normalized.followers || 0)
    const matchQuality = getMatchQuality(query, displayName, handle)

    return {
        key: getResultKey(platformId, creatorId),
        id: creatorId,
        platformId,
        displayName,
        handle,
        followers,
        avatarUrl: normalizeInstagramAvatarUrl(normalized.avatar_url, platformId),
        matchQuality,
        rankGroup: getRankGroup(matchQuality),
        source,
        sourceIndex: index,
        raw: creator,
        superview: {
            ...superview,
            _id: creatorId,
            platform_id: platformId,
            normalized: {
                ...normalized,
                avatar_url: normalizeInstagramAvatarUrl(normalized.avatar_url, platformId),
                name: handle || displayName,
                display_name: displayName || handle,
            },
        },
    }
}

const sortResults = (results) =>
    [...results].sort((a, b) => {
        if (a.rankGroup !== b.rankGroup) return a.rankGroup - b.rankGroup
        if (a.followers !== b.followers) return b.followers - a.followers
        return a.sourceIndex - b.sourceIndex
    })

const dedupeResults = (results) => {
    const resultsByKey = new Map()

    results.forEach((result) => {
        if (!result) return

        const existing = resultsByKey.get(result.key)
        if (!existing) {
            resultsByKey.set(result.key, result)
            return
        }

        const [bestResult] = sortResults([existing, result])
        resultsByKey.set(result.key, bestResult)
    })

    return sortResults([...resultsByKey.values()])
}

const appendUniqueResults = (currentResults, nextResults) => {
    const resultKeys = new Set(currentResults.map((result) => result.key))
    const mergedResults = [...currentResults]

    nextResults.forEach((result) => {
        if (!result || resultKeys.has(result.key)) return

        resultKeys.add(result.key)
        mergedResults.push(result)
    })

    return mergedResults
}

export function useGlobalCreatorSearch({ store, onError, initialPlatform } = {}) {
    const query = ref('')
    const selectedPlatform = ref(getPreferredGlobalSearchPlatformId(initialPlatform))
    const visibleResults = ref([])
    const pendingResults = ref([])
    const isLocalSearching = ref(false)
    const isExternalSearching = ref(false)
    const hasLocalSearched = ref(false)
    const hasExternalSearched = ref(false)

    const localCache = new Map()
    const externalCache = new Map()
    let searchRequestId = 0
    let skippedScheduledSearchSignature = null

    const trimmedQuery = computed(() => normalizeSearchText(query.value))
    const isSearchableQuery = computed(() => trimmedQuery.value.length >= LOCAL_SEARCH_MIN_CHARS)
    const selectedPlatformIds = computed(() => getPlatformIds(selectedPlatform.value))
    const hasPendingResults = computed(() => pendingResults.value.length > 0)
    const isSearching = computed(() => isLocalSearching.value || isExternalSearching.value)
    const isInitialLoading = computed(() => isLocalSearching.value && visibleResults.value.length === 0)
    const isLookingForCreators = computed(() => isSearching.value && !hasPendingResults.value)
    const canSearchMoreCreators = computed(
        () =>
            hasLocalSearched.value &&
            !hasExternalSearched.value &&
            !isSearching.value &&
            visibleResults.value.length > 0 &&
            pendingResults.value.length === 0,
    )
    const noCreatorsFound = computed(
        () =>
            isSearchableQuery.value &&
            hasLocalSearched.value &&
            !isSearching.value &&
            visibleResults.value.length === 0 &&
            pendingResults.value.length === 0,
    )

    const getCacheKey = (source, searchText, platformIds) =>
        `${source}:${normalizeForCompare(searchText)}:${platformIds.join(',')}`

    const getSearchSignature = (searchText = trimmedQuery.value, platformId = selectedPlatform.value) =>
        `${normalizeForCompare(searchText)}:${platformId}`

    const isCacheValid = (entry) => entry && Date.now() - entry.timestamp < CACHE_DURATION

    const cancelSearchRequests = () => {
        if (!store?.dispatch) return

        store.dispatch('CANCEL_PENDING_REQUESTS_BY_ENDPOINT', LOCAL_ENDPOINT)
        PLATFORM_IDS.forEach((platformId) => {
            store.dispatch('CANCEL_PENDING_REQUESTS_BY_ENDPOINT', getExternalEndpoint(platformId))
        })
    }

    const resetResults = () => {
        visibleResults.value = []
        pendingResults.value = []
        isLocalSearching.value = false
        isExternalSearching.value = false
        hasLocalSearched.value = false
        hasExternalSearched.value = false
    }

    const handleError = (error) => {
        if (error?.is_cancelled) return

        onError?.(error, {
            title: 'Creator Search',
            message: 'Error while searching creators.',
        })
    }

    const addRecentSearch = (searchText = trimmedQuery.value) => {
        const recentSearch = normalizeSearchText(searchText)

        if (recentSearch.length >= LOCAL_SEARCH_MIN_CHARS) {
            store?.commit?.('recentSearches:add', recentSearch)
        }
    }

    const removeRecentSearch = (searchText) => {
        store?.commit?.('recentSearches:remove', normalizeSearchText(searchText))
    }

    const clearRecentSearches = () => {
        store?.commit?.('recentSearches:clear')
    }

    const fetchLocalResults = async (searchText, platformIds) => {
        const cacheKey = getCacheKey('local', searchText, platformIds)
        const cachedResults = localCache.get(cacheKey)

        if (isCacheValid(cachedResults)) return cachedResults.results

        const response = await axios.post(LOCAL_ENDPOINT, {
            name: searchText,
            platform_ids: platformIds,
        })

        const results = _.map(response.data?.payload?.creators || [], (creator, index) =>
            normalizeCreatorResult(creator, {
                query: searchText,
                source: 'local',
                index,
            }),
        ).filter(Boolean)

        localCache.set(cacheKey, createCacheEntry(results))

        return results
    }

    const fetchExternalResultsForPlatform = async (searchText, platformId) => {
        const cacheKey = getCacheKey('external', searchText, [platformId])
        const cachedResults = externalCache.get(cacheKey)

        if (isCacheValid(cachedResults)) return cachedResults.results

        const endpoint = getExternalEndpoint(platformId)
        const response = await axios.post(endpoint, {
            name: searchText,
            platform: platformId,
        })

        const results = _.map(response.data?.payload?.creators || [], (creator, index) =>
            normalizeCreatorResult(creator, {
                query: searchText,
                source: 'external',
                index,
            }),
        ).filter(Boolean)

        externalCache.set(cacheKey, createCacheEntry(results))

        return results
    }

    const fetchExternalResults = async (searchText, platformIds) => {
        const externalResponses = await Promise.allSettled(
            platformIds.map((platformId) => fetchExternalResultsForPlatform(searchText, platformId)),
        )

        const results = externalResponses.flatMap((response) => {
            if (response.status === 'fulfilled') return response.value

            handleError(response.reason)
            return []
        })

        return results
    }

    const shouldRunExternalSearch = (results) => {
        const hasExactMatch = results.some((result) => result.matchQuality === 'exact')

        return results.length <= WEAK_RESULT_COUNT && !hasExactMatch
    }

    const getNewExternalResults = async (searchText, platformIds) => {
        const externalResults = dedupeResults(await fetchExternalResults(searchText, platformIds))
        const visibleKeys = new Set(visibleResults.value.map((result) => result.key))

        return externalResults.filter((result) => !visibleKeys.has(result.key))
    }

    const applyAutomaticExternalResults = async ({ requestId, searchText, platformIds }) => {
        const externalResults = await getNewExternalResults(searchText, platformIds)

        if (requestId !== searchRequestId) return false

        if (visibleResults.value.length > 0) {
            pendingResults.value = externalResults
        } else {
            visibleResults.value = appendUniqueResults(visibleResults.value, externalResults)
        }

        hasExternalSearched.value = true

        return externalResults.length > 0
    }

    const appendExternalResults = async ({ requestId, searchText, platformIds }) => {
        const externalResults = await getNewExternalResults(searchText, platformIds)

        if (requestId !== searchRequestId) return false

        visibleResults.value = appendUniqueResults(visibleResults.value, externalResults)
        hasExternalSearched.value = true

        return true
    }

    const searchNow = async () => {
        const searchText = trimmedQuery.value
        const platformIds = selectedPlatformIds.value
        const requestId = ++searchRequestId

        if (!isSearchableQuery.value) {
            cancelSearchRequests()
            resetResults()
            return
        }

        pendingResults.value = []
        hasExternalSearched.value = false
        isExternalSearching.value = false
        isLocalSearching.value = true
        hasLocalSearched.value = false
        cancelSearchRequests()

        try {
            const localResults = dedupeResults(await fetchLocalResults(searchText, platformIds))

            if (requestId !== searchRequestId) return

            visibleResults.value = localResults
            hasLocalSearched.value = true

            if (localResults.length > 0) addRecentSearch(searchText)

            if (!shouldRunExternalSearch(localResults)) return

            isExternalSearching.value = true

            const foundExternalResults = await applyAutomaticExternalResults({ requestId, searchText, platformIds })

            if (foundExternalResults && localResults.length === 0) addRecentSearch(searchText)
        } catch (error) {
            if (requestId === searchRequestId) {
                hasLocalSearched.value = true
            }
            handleError(error)
        } finally {
            if (requestId === searchRequestId) {
                isLocalSearching.value = false
                isExternalSearching.value = false
            }
        }
    }

    const scheduleSearch = _.debounce(searchNow, SEARCH_DEBOUNCE_MS)

    const searchImmediately = () => {
        scheduleSearch.cancel()
        return searchNow()
    }

    const setQueryAndSearchImmediately = (searchText) => {
        const normalizedSearchText = normalizeSearchText(searchText)
        const shouldSkipWatcher = trimmedQuery.value !== normalizedSearchText

        scheduleSearch.cancel()

        if (shouldSkipWatcher) {
            skippedScheduledSearchSignature = getSearchSignature(normalizedSearchText)
        }

        query.value = searchText

        return searchNow()
    }

    const persistSelectedPlatform = (platformId) => {
        if (!isSupportedPlatformId(platformId)) return

        store?.commit?.('preferences:set', {
            key: 'globalSearch.platformId',
            value: platformId,
        })
    }

    const applyPreferredPlatform = (platformId) => {
        const nextPlatformId = getPreferredGlobalSearchPlatformId(platformId)

        if (selectedPlatform.value !== nextPlatformId) {
            selectedPlatform.value = nextPlatformId
        }
    }

    const setSelectedPlatform = (platformId) => {
        const nextPlatformId = getPreferredGlobalSearchPlatformId(platformId)

        if (selectedPlatform.value !== nextPlatformId) {
            selectedPlatform.value = nextPlatformId
        }

        persistSelectedPlatform(nextPlatformId)
    }

    const searchMoreCreators = async () => {
        if (!canSearchMoreCreators.value) return

        const searchText = trimmedQuery.value
        const platformIds = selectedPlatformIds.value
        const requestId = ++searchRequestId

        scheduleSearch.cancel()
        cancelSearchRequests()
        pendingResults.value = []
        isExternalSearching.value = true

        try {
            const didApplyResults = await appendExternalResults({ requestId, searchText, platformIds })

            if (didApplyResults) addRecentSearch(searchText)
        } catch (error) {
            handleError(error)
        } finally {
            if (requestId === searchRequestId) {
                isExternalSearching.value = false
            }
        }
    }

    const showMoreResults = () => {
        visibleResults.value = appendUniqueResults(visibleResults.value, pendingResults.value)
        pendingResults.value = []

        if (visibleResults.value.length > 0) addRecentSearch()
    }

    const clearSearch = () => {
        query.value = ''
        scheduleSearch.cancel()
        cancelSearchRequests()
        resetResults()
    }

    watch([trimmedQuery, selectedPlatform], ([searchText, platformId]) => {
        const searchSignature = getSearchSignature(searchText, platformId)

        if (skippedScheduledSearchSignature === searchSignature) {
            skippedScheduledSearchSignature = null
            return
        }

        scheduleSearch()
    })

    return {
        query,
        selectedPlatform,
        visibleResults,
        pendingResults,
        isLocalSearching,
        isExternalSearching,
        hasLocalSearched,
        hasExternalSearched,
        trimmedQuery,
        isSearchableQuery,
        selectedPlatformIds,
        hasPendingResults,
        isSearching,
        isInitialLoading,
        isLookingForCreators,
        canSearchMoreCreators,
        noCreatorsFound,
        addRecentSearch,
        removeRecentSearch,
        clearRecentSearches,
        searchImmediately,
        setQueryAndSearchImmediately,
        setSelectedPlatform,
        applyPreferredPlatform,
        searchMoreCreators,
        showMoreResults,
        clearSearch,
    }
}
